// ---------------------------------------------------------------------------
// Metric Definitions — Shared Types & Default Content Module
//
// A typed, shared constant containing the default prose, icons, tier sets and
// "how to use this metric" tips for the metrics shown on the Skystone
// dashboards and Definitions Catalogue page.
//
// The content here is the canonical seed source. It is consumed by:
//   - frontend Definitions Catalogue page (US3) — renders immediately from this module
//   - backend DB migration (US4)        — seeds `metric_definitions` /
//                                          `metric_thresholds` from this module
//
// The prose matches `investigation/Skystone_Metrics_Guide.html`, and the DORA
// tier data is re-exported from `metric-thresholds.ts` (single source of truth
// from US1).
// ---------------------------------------------------------------------------

import {
  DEFAULT_DORA_THRESHOLDS,
  type IMetricTier,
  type MetricDirection,
} from './metric-thresholds.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type MetricGroupKey = 'engineering' | 'product' | 'code_quality' | 'governance';

/** One numbered "How to use this metric" tip. `**bold**` markdown is allowed. */
export interface IHowToUseTip {
  order: number; // 1-based
  text: string;
}

/** Full definition of a metric: prose, visual, directional semantics, tiers. */
export interface IMetricDefinition {
  metricKey: string;
  groupKey: MetricGroupKey;
  displayOrder: number; // order within its group
  name: string;
  tagline: string;
  definition: string;
  direction: MetricDirection;
  unit: string;
  icon: string; // emoji from the HTML guide
  sourceLabel: string;
  howToUseTips: IHowToUseTip[];
  tiers: IMetricTier[]; // [] for tier-less metrics (e.g. Throughput)
  /**
   * Ordered list of canonical values for the metric (e.g. canonical statuses
   * for statusCount, canonical issue types for issueTypeCompliance). Optional
   * — most metrics do not need this. `undefined` and `[]` mean "not
   * configured" and render an empty-state on cards that consume them.
   */
  canonicalValues?: string[];
  /**
   * Plain-language "how it's calculated" explanation. Admin-editable at
   * `/admin/metric-definitions` and surfaced as the info tooltip on the
   * Management View matrix cards (rides on `IMatrixCardSpec.calculation`).
   * Optional — `undefined` / empty means no tooltip is shown. Paragraph
   * breaks are `\n\n` and step breaks are `\n`; consumers render with
   * whitespace preserved.
   */
  calculation?: string;
  /**
   * Plain-language one-line caveat ("Watch out"). Admin-editable at
   * `/admin/metric-definitions` and rendered as its own callout on the
   * Definitions Catalogue card only — it is intentionally NOT shown in the
   * compact popover (the popover's nuance comes from the last sentence of
   * `definition`). Optional — `undefined` / empty means no callout is shown.
   */
  watchOut?: string;
}

/**
 * A historical snapshot of a metric definition (PRODOPSAI-651/652). One row per
 * superseded revision; the live `IMetricDefinition` is the current state. Used
 * by the admin version-history panel to list and restore past versions.
 */
export interface IMetricDefinitionVersion {
  metricKey: string;
  version: number;
  name: string;
  tagline: string;
  definition: string;
  howToUseTips: IHowToUseTip[];
  canonicalValues: string[] | null;
  calculation: string | null;
  watchOut: string | null;
  tiers: IMetricTier[];
  /** Why this version was archived: 'baseline' | 'admin_update' | 'admin_reset' | 'admin_restore'. */
  source: string;
  /** ISO-8601 timestamp the snapshot was taken. */
  createdAt: string;
  /** Username of the admin whose edit superseded this version; null for system/baseline rows. */
  createdBy: string | null;
}

/** A display group (Engineering / Product) with its metric definitions. */
export interface IMetricGroup {
  key: MetricGroupKey;
  label: string;
  sourceLabel: string;
  metrics: IMetricDefinition[];
}

// ---------------------------------------------------------------------------
// Source labels (constants — used in tests and in the content module below)
// ---------------------------------------------------------------------------

export const ENGINEERING_SOURCE_LABEL = 'DORA State of DevOps 2023';
export const PRODUCT_SOURCE_LABEL = 'Lean/Kanban research';
export const CODE_QUALITY_SOURCE_LABEL = 'SonarSource (SonarQube/SonarCloud)';
export const GOVERNANCE_SOURCE_LABEL = 'Engineering standards (org-wide)';

// ---------------------------------------------------------------------------
// Engineering (DORA) metrics
//
// The `tiers` arrays are re-used from `DEFAULT_DORA_THRESHOLDS` (US1) so there
// is exactly one place defining DORA tier boundaries.
// ---------------------------------------------------------------------------

const DEPLOY_FREQUENCY: IMetricDefinition = {
  metricKey: 'deployFrequency',
  groupKey: 'engineering',
  displayOrder: 1,
  name: 'Deployment Frequency',
  tagline: 'How often your team ships changes to production',
  definition:
    'How many unique code changes (commits) reach production per week — a change rolled out to several production environments still counts once.',
  direction: 'higher_better',
  unit: '/wk',
  icon: '🚀',
  sourceLabel: ENGINEERING_SOURCE_LABEL,
  howToUseTips: [
    {
      order: 1,
      text: '**Track trend over time**, not just the number. A rising frequency signals better automation and smaller batch sizes — both good things.',
    },
    {
      order: 2,
      text: '**Use it alongside CFR.** High frequency only matters if failure rate stays low. Frequency that spikes alongside failures means teams are cutting corners.',
    },
    {
      order: 3,
      text: '**For a COO or manager:** ask "what\'s stopping us deploying more often?" The answer usually reveals organisational friction — manual approvals, long QA cycles, or large release batches.',
    },
    {
      order: 4,
      text: '**One commit, one release.** A change deployed across multiple production stacks — e.g. one tenant prod each — counts as a single release. The metric measures cadence of distinct changes, not number of rollout events.',
    },
  ],
  tiers: DEFAULT_DORA_THRESHOLDS.deployFrequency.tiers,
  calculation: `Each time one of your commits lands in production it adds to that week's count, no matter how many production environments it's rolled out to (it's counted once). Failed deploys and non-production deploys don't count. This isn't driven by a Jira action — the number goes up when you ship small changes often, and down when work is held back into large, infrequent releases.`,
  watchOut: `Products with no production-deploy mapping show nothing here, so an org-level figure can describe only the mapped subset.`,
};

const LEAD_TIME: IMetricDefinition = {
  metricKey: 'leadTime',
  groupKey: 'engineering',
  displayOrder: 2,
  name: 'PR Lead Time (median)',
  tagline: "How long a pull request stays open before it's merged",
  definition:
    'The median time a pull request stays open — measured from when the PR is created (opened) to when it is merged into the main branch. It does not include the coding time before the PR is opened, nor the time from merge until the change reaches production. Lower is better.',
  direction: 'lower_better',
  unit: 'days',
  icon: '⏱',
  sourceLabel: ENGINEERING_SOURCE_LABEL,
  howToUseTips: [
    {
      order: 1,
      text: '**Look at review latency first.** Most of this number is time a PR spends waiting for review or re-review. Shrinking the time-to-first-review is usually the highest-value lever.',
    },
    {
      order: 2,
      text: '**Keep PRs small.** Large PRs sit open longer because they are harder to review. Smaller, more frequent PRs merge faster and pull this number down over quarters.',
    },
    {
      order: 3,
      text: '**For a COO:** this is how quickly proposed changes get accepted into the codebase. A shorter PR lead time means faster iteration once work is in flight.',
    },
  ],
  tiers: DEFAULT_DORA_THRESHOLDS.leadTime.tiers,
  calculation: `The clock starts when a PR is opened and stops when it is merged. We take the median across all merged PRs in the period (median, not mean, so a few very long-lived PRs don't distort the figure). It does not include the coding time before the PR is opened, or the time from merge until the code reaches production. Opening the PR late, or leaving it open through a long review, makes the number bigger; merging quickly makes it smaller.`,
};

const CFR: IMetricDefinition = {
  metricKey: 'cfr',
  groupKey: 'engineering',
  displayOrder: 3,
  name: 'Change Failure Rate (CFR)',
  tagline: 'How often production changes are followed by an incident',
  definition:
    'The number of incidents logged in the period as a percentage of the production changes (commits) shipped in the same period.',
  direction: 'lower_better',
  unit: '%',
  icon: '🛡',
  sourceLabel: ENGINEERING_SOURCE_LABEL,
  howToUseTips: [
    {
      order: 1,
      text: '**Always read alongside deploy frequency.** A high frequency with a low CFR is the gold standard. High frequency with a rising CFR is a warning sign.',
    },
    {
      order: 2,
      text: '**Segment by repository or team** to find which service or squad is driving failures — the overall average can hide a single chronic offender.',
    },
    {
      order: 3,
      text: '**For a COO:** CFR is the reliability promise to customers. A rising CFR means more incidents, more on-call burden and a worse user experience.',
    },
  ],
  tiers: DEFAULT_DORA_THRESHOLDS.cfr.tiers,
  calculation: `Every incident your team logs in the period counts against the number of changes you shipped. There is no link between a specific incident and a specific deploy — it's a ratio, not cause-and-effect. Logging an incident pushes the number up; not logging one (even when something broke) keeps it artificially low. With only a handful of incidents a month, a single logged incident can swing the percentage a lot.`,
  watchOut: `The value depends heavily on logging discipline — most products log zero incidents, which reads as a perfect score.`,
};

const MTTR: IMetricDefinition = {
  metricKey: 'mttr',
  groupKey: 'engineering',
  displayOrder: 4,
  name: 'Mean Time to Recover (MTTR)',
  tagline: 'How long incidents take to resolve, on average',
  definition:
    'The average length of the incidents logged in the period, in hours.',
  direction: 'lower_better',
  unit: 'hours',
  icon: '🔧',
  sourceLabel: ENGINEERING_SOURCE_LABEL,
  howToUseTips: [
    {
      order: 1,
      text: '**Look at detection time separately from fix time.** A long MTTR is often caused by slow detection (missing alerting), not a slow fix once the team is engaged.',
    },
    {
      order: 2,
      text: '**Review MTTR by service.** Services with chronic high MTTR usually lack runbooks, have unclear ownership, or have deep dependencies that make rollback hard.',
    },
    {
      order: 3,
      text: '**For a COO:** MTTR is the customer impact duration. Every hour of MTTR is an hour a user was affected. Reducing it is as important as reducing the failure rate itself.',
    },
  ],
  tiers: DEFAULT_DORA_THRESHOLDS.mttr.tiers,
  calculation: `When you log an incident with a start and a resolved time, its duration feeds this average. It's a mean, not a median, so one long incident pulls it up sharply. Incidents logged without a duration are skipped here (but still count toward Change Failure Rate). With only a few incidents a month, this is effectively "how long the most recent incident took".`,
  watchOut: `An outage logged with no end time disappears from this average while still counting against CFR.`,
};

// ---------------------------------------------------------------------------
// Product (Agile Flow) metrics
//
// Tier boundaries come from the HTML guide. Bound inclusivity mirrors the
// pattern used by DORA non-MTTR metrics: upperBound is inclusive for
// lower_better tiers (except the last, unbounded one), lowerBound is
// inclusive for higher_better tiers.
// ---------------------------------------------------------------------------

const EPIC_COMPLETION_TIERS: IMetricTier[] = [
  {
    tierId: 'on_target',
    tierOrder: 1,
    label: 'On target',
    lowerBound: 80,
    upperBound: null,
    lowerBoundInclusive: true,
    upperBoundInclusive: false,
    colorToken: 'success',
    description: '≥ 80% of scope delivered by mid-quarter',
  },
  {
    tierId: 'watch',
    tierOrder: 2,
    label: 'Watch',
    lowerBound: 65,
    upperBound: 80,
    lowerBoundInclusive: true,
    upperBoundInclusive: false,
    colorToken: 'warning',
    description: '65-80% — scope risk, may need re-plan',
  },
  {
    tierId: 'at_risk',
    tierOrder: 3,
    label: 'At risk',
    lowerBound: null,
    upperBound: 65,
    lowerBoundInclusive: false,
    upperBoundInclusive: false,
    colorToken: 'error',
    description: '< 65% — unlikely to deliver committed scope',
  },
];

const EPIC_COMPLETION: IMetricDefinition = {
  metricKey: 'epicCompletion',
  groupKey: 'product',
  displayOrder: 1,
  name: 'Epic Completion Rate',
  tagline: "How much of an Epic's current scope is done",
  definition:
    'The share of an Epic\'s child tickets that are currently Done. It reflects current scope, not a plan fixed at the start of the quarter.',
  direction: 'higher_better',
  unit: '%',
  icon: '✅',
  sourceLabel: PRODUCT_SOURCE_LABEL,
  howToUseTips: [
    {
      order: 1,
      text: "**Don't optimise purely for 100%.** Teams that always hit 100% are often under-committing. A healthy range is 75-90%, reflecting ambitious but realistic planning.",
    },
    {
      order: 2,
      text: '**Drill into which epics are behind.** A low average often hides two or three specific epics with blockers — these need a conversation, not a metric.',
    },
    {
      order: 3,
      text: '**For a COO:** this is the delivery promise metric. Use it in quarterly reviews to hold teams accountable and to identify where investment in capacity or prioritisation is needed.',
    },
  ],
  tiers: EPIC_COMPLETION_TIERS,
  calculation: `Every ticket you link under an Epic becomes part of that Epic's total. The rate goes up as children reach Done and down as you add new children. Cancelling or declining a child does not remove it — it stays in the total and keeps dragging the rate down. Because the rate reads the live set of children, adding work to an Epic mid-quarter lowers it even while you're delivering.`,
  watchOut: `Large catch-all "bucket" Epics will always sit low by design — read them as ongoing buckets, not delivery risks.`,
};

const CYCLE_TIME_P85_TIERS: IMetricTier[] = [
  {
    tierId: 'excellent',
    tierOrder: 1,
    label: 'Excellent',
    lowerBound: null,
    upperBound: 7,
    lowerBoundInclusive: false,
    upperBoundInclusive: true,
    colorToken: 'success',
    description: '≤ 7 days — short cycles, fast feedback',
  },
  {
    tierId: 'good',
    tierOrder: 2,
    label: 'Good',
    lowerBound: 7,
    upperBound: 14,
    lowerBoundInclusive: false,
    upperBoundInclusive: true,
    colorToken: 'primary',
    description: '7-14 days — healthy delivery rhythm',
  },
  {
    tierId: 'watch',
    tierOrder: 3,
    label: 'Watch',
    lowerBound: 14,
    upperBound: 21,
    lowerBoundInclusive: false,
    upperBoundInclusive: true,
    colorToken: 'warning',
    description: '14-21 days — work may be too large or blocked',
  },
  {
    tierId: 'slow',
    tierOrder: 4,
    label: 'Slow',
    lowerBound: 21,
    upperBound: null,
    lowerBoundInclusive: false,
    upperBoundInclusive: false,
    colorToken: 'error',
    description: '> 21 days — high chance of scope creep and rework',
  },
];

const CYCLE_TIME_P85: IMetricDefinition = {
  metricKey: 'cycleTimeP85',
  groupKey: 'product',
  displayOrder: 2,
  name: 'P85 Cycle Time',
  tagline: 'How long it takes to complete a piece of work',
  definition:
    'The 85th-percentile time a Story, Bug or Task takes from the first time it enters In Progress to the last time it reaches Done.',
  direction: 'lower_better',
  unit: 'days',
  icon: '🔄',
  sourceLabel: PRODUCT_SOURCE_LABEL,
  howToUseTips: [
    {
      order: 1,
      text: '**Use it to right-size stories.** If P85 is consistently > 14 days, stories are likely too large. Breaking them into smaller slices reduces cycle time and improves predictability.',
    },
    {
      order: 2,
      text: '**Compare by type.** Bugs taking longer than stories often signals missing test coverage. Tasks taking long may indicate unclear ownership.',
    },
    {
      order: 3,
      text: '**For a COO:** cycle time is a leading indicator of delivery velocity. A rising P85 is a warning that delivery will slow before it shows up in output metrics like throughput.',
    },
  ],
  tiers: CYCLE_TIME_P85_TIERS,
  calculation: `The clock starts the first time you move a ticket into In Progress and stops the last time you move it to Done. It never pauses — time the ticket spends sitting in the backlog after you started it still counts. If you reopen a finished ticket and close it again later, that later Done becomes the new stop point, so the number grows. Tickets that jump straight to Done without ever being In Progress aren't counted at all. "P85" means 85 out of 100 tickets finish within this many days.`,
  watchOut: `Don't flag work as In Progress before you'll actually work on it — the idle time counts in full.`,
};

const WIP_TIERS: IMetricTier[] = [
  {
    tierId: 'healthy',
    tierOrder: 1,
    label: 'Healthy',
    lowerBound: null,
    upperBound: 2,
    lowerBoundInclusive: false,
    upperBoundInclusive: true,
    colorToken: 'success',
    description: '1-2 items in progress per developer',
  },
  {
    tierId: 'watch',
    tierOrder: 2,
    label: 'Watch',
    lowerBound: 2,
    upperBound: 3,
    lowerBoundInclusive: false,
    upperBoundInclusive: true,
    colorToken: 'warning',
    description: '2-3 items — context-switching begins to hurt',
  },
  {
    tierId: 'too_high',
    tierOrder: 3,
    label: 'Too high',
    lowerBound: 3,
    upperBound: null,
    lowerBoundInclusive: false,
    upperBoundInclusive: false,
    colorToken: 'error',
    description: '> 3 per person — flow is severely disrupted',
  },
];

const WIP: IMetricDefinition = {
  metricKey: 'wip',
  groupKey: 'product',
  displayOrder: 3,
  name: 'Work in Progress (WIP)',
  tagline: 'How many tickets are being worked on right now',
  definition:
    'How many Story/Task/Bug tickets are in an in-progress status on the latest daily snapshot.',
  direction: 'lower_better',
  unit: 'items',
  icon: '🔀',
  sourceLabel: PRODUCT_SOURCE_LABEL,
  howToUseTips: [
    {
      order: 1,
      text: '**Set an explicit WIP limit** (e.g. team size × 1.5). When the limit is hit, the rule is simple: finish before you start. This is the single highest-impact Lean practice.',
    },
    {
      order: 2,
      text: '**Rising WIP alongside rising cycle time** is the clearest signal that flow is broken. These two metrics should always be read together.',
    },
    {
      order: 3,
      text: '**For a COO:** WIP is a proxy for organisational pressure. High WIP often means teams are being asked to work on too many priorities at once — a structural, not engineering, problem.',
    },
  ],
  tiers: WIP_TIERS,
  calculation: `A ticket counts from the moment you move it into an in-progress status. But only the statuses the system recognises as "in progress" count — if your board uses a custom status such as Developing or On Hold, those tickets won't appear here even though you're working on them. Sub-tasks and Epics never count. The healthy / too-high colour bands are defined per developer, but no team headcount is available, so today the number is shown with no colour.`,
  watchOut: `The healthy / too-high bands are defined "per developer", but no team headcount exists, so the number shows with no colour today.`,
};

const THROUGHPUT: IMetricDefinition = {
  metricKey: 'throughput',
  groupKey: 'product',
  displayOrder: 4,
  name: 'Throughput',
  tagline: 'How much the team completes per week',
  definition:
    'The number of Story/Task/Bug tickets finished in the period.',
  direction: 'higher_better',
  unit: 'items/wk',
  icon: '📈',
  sourceLabel: PRODUCT_SOURCE_LABEL,
  howToUseTips: [
    {
      order: 1,
      text: '**Use throughput for forecasting.** If the team completes 80 stories per month and a feature needs 40 stories, it will take approximately 2 months — no estimation sessions needed.',
    },
    {
      order: 2,
      text: '**Watch for throughput rising while cycle time also rises.** This can mean the team is completing small easy items but leaving the complex ones stuck — a false positive.',
    },
    {
      order: 3,
      text: '**For a COO:** throughput is the honest delivery rate. It gives a data-based answer to "how long will this take?" without depending on subjective estimates or story points.',
    },
  ],
  // Throughput is explicitly tier-less — benchmarks are team-specific per the
  // HTML guide. Dashboards render the value without a classification badge.
  tiers: [],
  calculation: `Move a Story, Task or Bug to Done and it adds 1 to the period it was completed in. Cancelled and declined tickets never count. Because the number reads each ticket's current status, cancelling or re-opening a ticket later can change a past period's count after the fact — a board clean-up that re-statuses old tickets can make a past month's throughput drop. Sub-tasks aren't counted.`,
  watchOut: `A bulk re-status (e.g. cancelling many old done tickets on one day) silently rewrites earlier months.`,
};

// ---------------------------------------------------------------------------
// Code Quality (SonarSource) metrics
//
// Numeric metrics (`cq_coverage`, `cq_qg_pass_rate`) use higher_better with
// percent-based tiers. Rating metrics (`cq_reliability_rating`,
// `cq_maintainability_rating`) use lower_better on SonarSource's numeric
// 1..5 scale where 1 = A (best) and 5 = E (worst). Bound inclusivity for
// ratings mirrors MTTR's strict-< pattern so each integer 1..5 lands in
// exactly one tier.
// ---------------------------------------------------------------------------

const CQ_COVERAGE_TIERS: IMetricTier[] = buildHigherBetterPercentTiers([
  { tierId: 'excellent', label: 'Excellent', lowerBound: 80,   upperBound: null, colorToken: 'success', description: '≥ 80% — strong test coverage on critical paths' },
  { tierId: 'good',      label: 'Good',      lowerBound: 60,   upperBound: 80,   colorToken: 'primary', description: '60-80% — most logic covered, some gaps remain' },
  { tierId: 'fair',      label: 'Fair',      lowerBound: 30,   upperBound: 60,   colorToken: 'warning', description: '30-60% — meaningful blind spots in the test suite' },
  { tierId: 'low',       label: 'Low',       lowerBound: null, upperBound: 30,   colorToken: 'error',   description: '< 30% — most code paths untested' },
]);

const CQ_COVERAGE: IMetricDefinition = {
  metricKey: 'cq_coverage',
  groupKey: 'code_quality',
  displayOrder: 1,
  name: 'Overall Coverage',
  tagline: 'How much of the code is exercised by automated tests',
  definition:
    'The percentage of code covered by automated tests, per repository.',
  direction: 'higher_better',
  unit: '%',
  icon: '🧪',
  sourceLabel: CODE_QUALITY_SOURCE_LABEL,
  howToUseTips: [
    {
      order: 1,
      text: '**Watch coverage on new code, not the absolute number.** A team raising new-code coverage to 80% steadily improves the codebase even if the overall figure looks flat.',
    },
    {
      order: 2,
      text: '**Look at uncovered files, not just the average.** A high overall figure can hide a critical service with no tests. Drill in via SonarCloud to find the worst offenders.',
    },
    {
      order: 3,
      text: '**For a COO:** coverage is the safety net behind every deployment. Investing in tests pays off as fewer production incidents and faster, lower-risk releases.',
    },
  ],
  tiers: CQ_COVERAGE_TIERS,
  calculation: `Writing tests that exercise your code raises this; adding code without tests lowers it. It's overall coverage of the whole codebase, not just new code. The team figure is a simple average across the team's repos, so a tiny utility repo counts as much as the main app — one poorly-covered small repo can pull the team number down. (This isn't a Jira action — it moves when you write or skip tests.)`,
  watchOut: `Only Sonar-onboarded repos count, and most of the repo estate has no Sonar project — the org figure describes a minority of repos.`,
};

const CQ_QG_PASS_RATE_TIERS: IMetricTier[] = buildHigherBetterPercentTiers([
  { tierId: 'excellent', label: 'Excellent', lowerBound: 80,   upperBound: null, colorToken: 'success', description: '≥ 80% — most analyses pass the gate first time' },
  { tierId: 'good',      label: 'Good',      lowerBound: 50,   upperBound: 80,   colorToken: 'primary', description: '50-80% — gate failures are common but not the norm' },
  { tierId: 'watch',     label: 'Watch',     lowerBound: 20,   upperBound: 50,   colorToken: 'warning', description: '20-50% — gate failures dominate; rules may be too strict or quality slipping' },
  { tierId: 'failing',   label: 'Failing',   lowerBound: null, upperBound: 20,   colorToken: 'error',   description: '< 20% — gate failure is the default outcome' },
]);

const CQ_QG_PASS_RATE: IMetricDefinition = {
  metricKey: 'cq_qg_pass_rate',
  groupKey: 'code_quality',
  displayOrder: 2,
  name: 'Quality Gate Pass Rate',
  tagline: 'Whether repositories pass their SonarCloud Quality Gate',
  definition:
    'Intended: the share of repositories whose SonarCloud Quality Gate passes. Not currently operational — see how it works.',
  direction: 'higher_better',
  unit: '%',
  icon: '✅',
  sourceLabel: CODE_QUALITY_SOURCE_LABEL,
  howToUseTips: [
    {
      order: 1,
      text: '**Read it alongside the gate definition.** A low pass rate against a tough gate may be a *good* sign — it means the gate is doing its job. A 100% pass rate against a permissive gate is meaningless.',
    },
    {
      order: 2,
      text: '**Look for the most-tripped condition.** Often a single rule (e.g. new-code coverage < 80%) drives most failures — fixing that one thing lifts the whole rate.',
    },
    {
      order: 3,
      text: '**For a COO:** the gate pass rate is the team\'s self-assessment. A falling pass rate is an early warning that quality is slipping before it reaches users.',
    },
  ],
  tiers: CQ_QG_PASS_RATE_TIERS,
  calculation: `This is meant to show how many repositories pass their SonarCloud Quality Gate. Right now the synced field holds a health label (Healthy / Critical / Needs Attention) rather than the actual gate result, so the metric can't be computed and shows 0/0 everywhere. Until the real gate status is synced, treat this tile as "no data", not as a real pass rate.`,
  watchOut: `A 0/0 looks like "no Sonar data" — it actually means the metric can't be computed at all yet.`,
};

// SonarSource numeric rating scale: 1 = A (best), 5 = E (worst). All A–E
// metrics share the same bound geometry (each integer 1..5 lands in exactly
// one tier via strict-> on lower / inclusive-≤ on upper). Only the per-tier
// description varies between rating metrics.
type SonarRatingTierBase = Omit<IMetricTier, 'description'>;

const SONAR_RATING_TIER_GEOMETRY: readonly SonarRatingTierBase[] = [
  { tierId: 'a', tierOrder: 1, label: 'A', lowerBound: null, upperBound: 1,    lowerBoundInclusive: false, upperBoundInclusive: true,  colorToken: 'success' },
  { tierId: 'b', tierOrder: 2, label: 'B', lowerBound: 1,    upperBound: 2,    lowerBoundInclusive: false, upperBoundInclusive: true,  colorToken: 'primary' },
  { tierId: 'c', tierOrder: 3, label: 'C', lowerBound: 2,    upperBound: 3,    lowerBoundInclusive: false, upperBoundInclusive: true,  colorToken: 'warning' },
  { tierId: 'd', tierOrder: 4, label: 'D', lowerBound: 3,    upperBound: 4,    lowerBoundInclusive: false, upperBoundInclusive: true,  colorToken: 'error'   },
  { tierId: 'e', tierOrder: 5, label: 'E', lowerBound: 4,    upperBound: null, lowerBoundInclusive: false, upperBoundInclusive: false, colorToken: 'error'   },
];

function buildSonarRatingTiers(
  descriptions: readonly [string, string, string, string, string],
): IMetricTier[] {
  return SONAR_RATING_TIER_GEOMETRY.map((base, i) => ({ ...base, description: descriptions[i] }));
}

// Compact spec for higher_better percent-based tiers. tierOrder is derived
// from array index; bound inclusivity is derived from whether lowerBound is
// null (the unbounded-below "worst" tier on a higher_better metric uses
// strict-> rather than ≥). Centralising this geometry keeps the per-metric
// tier arrays declarative and avoids structural duplication across metrics.
type HigherBetterPercentTierSpec = Pick<
  IMetricTier,
  'tierId' | 'label' | 'lowerBound' | 'upperBound' | 'colorToken' | 'description'
>;

function buildHigherBetterPercentTiers(
  specs: readonly HigherBetterPercentTierSpec[],
): IMetricTier[] {
  return specs.map((spec, i) => ({
    ...spec,
    tierOrder: i + 1,
    lowerBoundInclusive: spec.lowerBound !== null,
    upperBoundInclusive: false,
  }));
}

const CQ_RELIABILITY_RATING_TIERS: IMetricTier[] = buildSonarRatingTiers([
  '0 bugs',
  'At least one minor bug',
  'At least one major bug',
  'At least one critical bug',
  'At least one blocker bug',
]);

const CQ_RELIABILITY_RATING: IMetricDefinition = {
  metricKey: 'cq_reliability_rating',
  groupKey: 'code_quality',
  displayOrder: 3,
  name: 'Reliability Rating',
  tagline: "Sonar's A–E grade for the worst open bug",
  definition:
    'A SonarCloud A–E grade per repository, set by the worst open bug (A = no bugs, E = at least one blocker).',
  direction: 'lower_better',
  unit: 'rating',
  icon: '🛡️',
  sourceLabel: CODE_QUALITY_SOURCE_LABEL,
  howToUseTips: [
    {
      order: 1,
      text: '**Anything below A means at least one open bug.** The letter is a high-water mark — fixing the worst single bug is what changes the rating.',
    },
    {
      order: 2,
      text: '**Pair with the bug count, not just the rating.** D and E both mean "real bug present" but the count tells you the breadth of the problem.',
    },
    {
      order: 3,
      text: "**For a COO:** an A-rated codebase is the goal — it means SonarSource sees no open bugs. A persistent C/D/E across services indicates structural quality issues, not isolated incidents.",
    },
  ],
  tiers: CQ_RELIABILITY_RATING_TIERS,
  calculation: `Fixing the worst open bug Sonar has flagged is what changes this grade — it's a high-water mark, not an average. A single critical bug drops the whole repo to D; clear it and the grade recovers. (Moves when you fix or introduce bugs in code, not via a Jira action.)`,
  watchOut: `The grade tells you severity, not breadth — pair it with the bug count.`,
};

const CQ_MAINTAINABILITY_RATING_TIERS: IMetricTier[] = buildSonarRatingTiers([
  '≤ 5% technical debt ratio',
  '5-10% technical debt ratio',
  '10-20% technical debt ratio',
  '20-50% technical debt ratio',
  '≥ 50% technical debt ratio',
]);

const CQ_MAINTAINABILITY_RATING: IMetricDefinition = {
  metricKey: 'cq_maintainability_rating',
  groupKey: 'code_quality',
  displayOrder: 4,
  name: 'Maintainability Rating',
  tagline: "Sonar's A–E grade for accumulated technical debt",
  definition:
    'A SonarCloud A–E grade per repository based on the technical-debt ratio (A = ≤5% debt, E = ≥50%).',
  direction: 'lower_better',
  unit: 'rating',
  icon: '🛠️',
  sourceLabel: CODE_QUALITY_SOURCE_LABEL,
  howToUseTips: [
    {
      order: 1,
      text: '**Falling from B to C usually means new debt was added without remediation.** Watch the rating across releases — small slips compound.',
    },
    {
      order: 2,
      text: '**Use the technical-debt ratio for the actual conversation.** The letter is a summary; the underlying ratio (and the issues feeding it) is what guides where to invest cleanup time.',
    },
    {
      order: 3,
      text: '**For a COO:** maintainability is a leading indicator of future delivery cost. A D-rated codebase will be slower and riskier to extend than a B-rated one — even if today\'s velocity looks similar.',
    },
  ],
  tiers: CQ_MAINTAINABILITY_RATING_TIERS,
  calculation: `Adding code Sonar considers hard to maintain raises the debt ratio and can drop the grade; cleaning up flagged issues raises it. The letter is just a summary of the underlying debt ratio. (Moves with code changes, not Jira actions.)`,
  watchOut: `On current data nearly every graded repo is an A, so this axis carries little signal right now.`,
};

// ---------------------------------------------------------------------------
// Governance (Workflow Hygiene) metrics
//
// Surface adherence to engineering standards on the Product Flow tab so teams
// can spot drift inside the same view they already use for delivery.
// `canonicalValues` on statusCount and issueTypeCompliance is the org-wide
// spine that drives the "non-canonical" tag lists on the cards.
// ---------------------------------------------------------------------------

const EPIC_LINKAGE_TIERS: IMetricTier[] = buildHigherBetterPercentTiers([
  { tierId: 'on_target', label: 'On target', lowerBound: 90,   upperBound: null, colorToken: 'success', description: '≥ 90% of items linked to a parent Epic' },
  { tierId: 'watch',     label: 'Watch',     lowerBound: 80,   upperBound: 90,   colorToken: 'warning', description: '80-90% — linkage drifting; address before it spreads' },
  { tierId: 'at_risk',   label: 'At risk',   lowerBound: null, upperBound: 80,   colorToken: 'error',   description: '< 80% — too many items without a parent Epic' },
]);

const EPIC_LINKAGE: IMetricDefinition = {
  metricKey: 'epicLinkage',
  groupKey: 'governance',
  displayOrder: 1,
  name: 'Epic Linkage',
  tagline: 'Share of tickets linked to a parent Epic',
  definition:
    'The share of the tickets you created this period that are linked to a parent Epic (Epics themselves are excluded; Sub-tasks are included).',
  direction: 'higher_better',
  unit: '%',
  icon: '🔗',
  sourceLabel: GOVERNANCE_SOURCE_LABEL,
  howToUseTips: [
    {
      order: 1,
      text: '**Linkage is a planning hygiene signal, not a productivity metric.** A drop usually means a refinement gap — work was picked up before being attached to an Epic.',
    },
    {
      order: 2,
      text: '**Pair it with Epic Completion Rate.** Linked work feeds the rate; unlinked work cannot. Rising linkage with steady completion is the healthy pattern.',
    },
    {
      order: 3,
      text: '**For a manager:** when linkage falls, check whether a single new initiative is being run as one-off tickets. Creating an Epic for it usually fixes the metric and the conversation.',
    },
  ],
  tiers: EPIC_LINKAGE_TIERS,
  calculation: `When you create a Story, Task or Bug, link it to a parent Epic. Every ticket you leave unlinked lowers your team's percentage for the month you created it; linking it later counts retroactively and fixes that month. Sub-tasks count here too — and they almost never get an Epic link — so a month with many sub-tasks looks worse even when the real work is linked. A link to an Epic that's been deleted or archived counts as unlinked.`,
  watchOut: `Because sub-tasks are included but never linked, the gap between teams is mostly sub-task volume, not real linkage.`,
};

const STATUS_COUNT_TIERS: IMetricTier[] = [
  {
    tierId: 'lean',
    tierOrder: 1,
    label: 'Lean',
    lowerBound: null,
    upperBound: 8,
    lowerBoundInclusive: false,
    upperBoundInclusive: true,
    colorToken: 'success',
    description: '≤ 8 distinct statuses — workflow stays readable',
  },
  {
    tierId: 'watch',
    tierOrder: 2,
    label: 'Watch',
    lowerBound: 8,
    upperBound: 12,
    lowerBoundInclusive: false,
    upperBoundInclusive: true,
    colorToken: 'warning',
    description: '9-12 statuses — workflow is starting to bloat',
  },
  {
    tierId: 'too_many',
    tierOrder: 3,
    label: 'Too many',
    lowerBound: 12,
    upperBound: null,
    lowerBoundInclusive: false,
    upperBoundInclusive: false,
    colorToken: 'error',
    description: '> 12 statuses — workflow is hard to read and report on',
  },
];

const STATUS_COUNT: IMetricDefinition = {
  metricKey: 'statusCount',
  groupKey: 'governance',
  displayOrder: 2,
  name: 'Status Count',
  tagline: "How many distinct statuses the team's workflow uses",
  definition:
    'The number of distinct statuses your team\'s tickets passed through during the period — fewer is better.',
  direction: 'lower_better',
  unit: 'statuses',
  icon: '📋',
  sourceLabel: GOVERNANCE_SOURCE_LABEL,
  howToUseTips: [
    {
      order: 1,
      text: '**Aim for the canonical spine plus the minimum needed extras.** Each extra status is a coordination cost — both for the team and for cross-team reporting.',
    },
    {
      order: 2,
      text: '**Look at the non-canonical list, not just the count.** Often one or two statuses (e.g. "Waiting on PM", "Blocked by Other Team") drive most of the deviation and can be retired or merged.',
    },
    {
      order: 3,
      text: '**For a manager:** rising status count is a leading indicator of a workflow that is starting to model exceptions instead of normal flow. Retire statuses faster than you add them.',
    },
  ],
  tiers: STATUS_COUNT_TIERS,
  canonicalValues: ['To Do', 'In Progress', 'In Review', 'Done'],
  calculation: `Every distinct status your tickets touch in the period is counted once — both statuses they moved into and statuses they moved out of. Adding a one-off status, or cleaning up your board, can push the count up for that month, because the statuses you moved away from still count. A bulk re-organisation (many tickets re-statused on a single day) can spike it with no change in how you actually work. Statuses outside the standard spine are flagged but still counted.`,
  watchOut: `The canonical spine is Backlog · To Do · In Progress · In Review · Testing · Done (updated 2026-06-11); near-miss names like "Test" vs "Testing" are flagged but matched exactly, with no aliases.`,
};

const ISSUE_TYPE_COMPLIANCE_TIERS: IMetricTier[] = buildHigherBetterPercentTiers([
  { tierId: 'on_target', label: 'On target', lowerBound: 100,  upperBound: null, colorToken: 'success', description: '100% — every in-scope item uses a canonical type' },
  { tierId: 'watch',     label: 'Watch',     lowerBound: 90,   upperBound: 100,  colorToken: 'warning', description: '90-99% — small share of non-canonical types' },
  { tierId: 'at_risk',   label: 'At risk',   lowerBound: null, upperBound: 90,   colorToken: 'error',   description: '< 90% — non-canonical types are common' },
]);

const ISSUE_TYPE_COMPLIANCE: IMetricDefinition = {
  metricKey: 'issueTypeCompliance',
  groupKey: 'governance',
  displayOrder: 3,
  name: 'Issue-Type Compliance',
  tagline: 'Share of tickets using a standard issue type',
  definition:
    'The share of the tickets you created this period that use a standard type (Epic, Story, Task, Bug).',
  direction: 'higher_better',
  unit: '%',
  icon: '🏷️',
  sourceLabel: GOVERNANCE_SOURCE_LABEL,
  howToUseTips: [
    {
      order: 1,
      text: '**Check the non-canonical breakdown first.** If most of the deviation is one type (e.g. Sub-task), the conversation is whether to fold it into the canonical set or to retire it.',
    },
    {
      order: 2,
      text: '**Read it alongside Epic Linkage.** Both metrics surface vocabulary drift — together they are a snapshot of refinement quality.',
    },
    {
      order: 3,
      text: '**For a manager:** persistent <100% compliance is rarely about discipline; it usually means the canonical set does not match how the team thinks. Update the canonical set rather than nag the team.',
    },
  ],
  tiers: ISSUE_TYPE_COMPLIANCE_TIERS,
  canonicalValues: ['Epic', 'Story', 'Task', 'Bug'],
  calculation: `Create a ticket with one of the standard types and it's compliant. Every Sub-task you create lowers the number, because Sub-task isn't on the standard list — so in practice this metric currently measures "how few sub-tasks you use". The match is exact and case-sensitive, so a mistyped type like "bug" would also count as non-standard. Reaching green requires 100%.`,
  watchOut: `Teams that use sub-tasks have a hard ceiling below green, even with otherwise clean vocabulary.`,
};

const INCIDENT_LOGGING_COVERAGE_TIERS: IMetricTier[] = buildHigherBetterPercentTiers([
  { tierId: 'on_target', label: 'On target', lowerBound: 90,   upperBound: null, colorToken: 'success', description: '≥ 90% of production deploys have a linked incident record' },
  { tierId: 'watch',     label: 'Watch',     lowerBound: 80,   upperBound: 90,   colorToken: 'warning', description: '80-89% — coverage drifting; address before DORA reliability is impacted' },
  { tierId: 'at_risk',   label: 'At risk',   lowerBound: null, upperBound: 80,   colorToken: 'error',   description: '< 80% — DORA metrics may be unreliable; too many deploys lack an incident record' },
]);

const INCIDENT_LOGGING_COVERAGE: IMetricDefinition = {
  metricKey: 'incidentLoggingCoverage',
  groupKey: 'governance',
  displayOrder: 4,
  name: 'Incident Logging Coverage',
  tagline: 'Share of production deploys with an incident record',
  definition:
    'Intended: the share of production deploys that have a linked incident record (or an explicit "no incident" note). Not currently operational — see how it works.',
  direction: 'higher_better',
  unit: '%',
  icon: '🛡️',
  sourceLabel: GOVERNANCE_SOURCE_LABEL,
  howToUseTips: [
    {
      order: 1,
      text: '**Coverage is the trust dial on DORA.** Change Failure Rate and MTTR are only as honest as the share of deploys that get an incident record (or an explicit no-incident attestation) attached.',
    },
    {
      order: 2,
      text: '**Aim for ≥ 90%; treat 80% as the floor.** Coverage below 80% triggers the "DORA may be unreliable" banner — the engineering metrics keep rendering, but with a caveat that they understate real failure rates.',
    },
    {
      order: 3,
      text: '**For a manager:** falling coverage is rarely about discipline; it usually means the deploy → incident workflow has a gap (e.g. a new pipeline that does not prompt for an attestation). Fix the workflow, not the team.',
    },
  ],
  tiers: INCIDENT_LOGGING_COVERAGE_TIERS,
  calculation: `This is meant to be the trust dial for Change Failure Rate and MTTR — if most deploys have an incident record (or a "nothing broke" note) attached, those two metrics are trustworthy. There is currently no link between deploys and incidents in the data, and the warning banner isn't shown, so the coverage can't be computed today. Until it is, read CFR and MTTR knowing they may understate real failures.`,
  watchOut: `Its absence means CFR and MTTR currently render with no trust signal at all.`,
};

// ---------------------------------------------------------------------------
// Exported content
//
// Sorted by (groupKey, displayOrder). Consumers that want grouped shape can
// build it from this flat array in a single pass.
// ---------------------------------------------------------------------------

export const DEFAULT_METRIC_DEFINITIONS: readonly IMetricDefinition[] = [
  // engineering (DORA) — displayOrder 1..4
  DEPLOY_FREQUENCY,
  LEAD_TIME,
  CFR,
  MTTR,
  // product (Agile Flow) — displayOrder 1..4
  EPIC_COMPLETION,
  CYCLE_TIME_P85,
  WIP,
  THROUGHPUT,
  // code_quality (SonarSource) — displayOrder 1..4
  CQ_COVERAGE,
  CQ_QG_PASS_RATE,
  CQ_RELIABILITY_RATING,
  CQ_MAINTAINABILITY_RATING,
  // governance (Workflow Hygiene) — displayOrder 1..4
  EPIC_LINKAGE,
  STATUS_COUNT,
  ISSUE_TYPE_COMPLIANCE,
  INCIDENT_LOGGING_COVERAGE,
];
